import React, { useEffect, useState } from 'react';

import { useConfiguration } from '../../../../context/configurationContext';
import BlockContainer from '../BlockContainer';

const LINK_PATTERN = /\[([^\]]+)\]\(([^)\s]+)\)/g;

const Icon = ({ children, label }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    {children}
    <title>{label}</title>
  </svg>
);

const InstagramIcon = () => (
  <Icon label="Instagram">
    <rect x="3" y="3" width="18" height="18" rx="5" fill='none' stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="4" fill='none' stroke="currentColor" strokeWidth="1.8" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
  </Icon>
);

const LinkIcon = () => (
  <Icon label="Link">
    <path
      d="M9 17H7a5 5 0 0 1 0-10h2M15 7h2a5 5 0 0 1 0 10h-2M8 12h8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      fill='none'
    />
  </Icon>
);

const XIcon = () => (
  <Icon label="X">
    <path
      d="M17.5 3h3.1l-6.8 7.8L22 21h-6.2l-4.8-6.3L5.7 21H2.6l7.3-8.3L2 3h6.3l4.4 5.8L17.5 3Zm-1.1 16.2h1.7L7.7 4.7H5.9l10.5 14.5Z"
      fill="currentColor"
    />
  </Icon>
);

const ShareIcon = () => (
  <Icon label="Share">
    <circle fill='none' cx="18" cy="5" r="2.2" stroke="currentColor" strokeWidth="1.8" />
    <circle fill='none' cx="6" cy="12" r="2.2" stroke="currentColor" strokeWidth="1.8" />
    <circle fill='none' cx="18" cy="19" r="2.2" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M8.2 13.1 15.8 17.4M15.8 6.6 8.2 10.9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </Icon>
);

const CopyIcon = () => (
  <Icon label="Copy link">
    <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" fill='none' />
    <path
      d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      fill='none'
    />
  </Icon>
);

const ICONS = {
  instagram: InstagramIcon,
  link: LinkIcon,
  x: XIcon,
  share: ShareIcon,
  copy: CopyIcon,
};

/**
 * Pulls `[Label](url)` pairs out of the Console markdown on this block.
 * Labels match Instagram, Link, X/Twitter, Share, and Copy.
 */
const linksFromMarkdown = markdown => {
  const found = {};
  if (typeof markdown !== 'string') {
    return found;
  }
  const pattern = new RegExp(LINK_PATTERN.source, 'g');
  let match = pattern.exec(markdown);
  while (match) {
    const label = match[1].trim().toLowerCase();
    const href = match[2];
    if (label.includes('insta')) {
      found.instagram = href;
    } else if (label === 'x' || label.includes('twitter')) {
      found.x = href;
    } else if (label.includes('share') || label.includes('facebook')) {
      found.share = href;
    } else if (label.includes('copy')) {
      found.copy = href;
    } else if (label.includes('link')) {
      found.link = href;
    }
    match = pattern.exec(markdown);
  }
  return found;
};

const twitterPage = handle => {
  if (!handle) {
    return null;
  }
  const name = handle.charAt(0) === '@' ? handle.slice(1) : handle;
  return `https://twitter.com/${name}`;
};

/**
 * Share row for a journal article. Console block id: `social-links`.
 * A markdown link on the block is the destination. Without one, Instagram,
 * X, and Share open that network with this page's address, Link opens the
 * page, and Copy places the address on the clipboard.
 *
 * @component
 * @param {Object} props
 * @param {string} props.blockId
 * @param {Object?} props.text markdown field from the block
 * @returns {JSX.Element}
 */
const JournalSocialLinks = ({ blockId = 'social-links', text }) => {
  const config = useConfiguration();
  const [pageUrl, setPageUrl] = useState('');
  const [pageTitle, setPageTitle] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setPageUrl(window.location.href);
    const heading = document.querySelector('#detail-journal-hero-content h1');
    setPageTitle(heading?.textContent?.trim() || document.title);
  }, []);

  const provided = linksFromMarkdown(text?.content);
  const encodedUrl = encodeURIComponent(pageUrl);
  const encodedTitle = encodeURIComponent(pageTitle);
  const items = [
    {
      id: 'instagram',
      label: 'Instagram',
      href: provided.instagram || config?.siteInstagramPage || 'https://www.instagram.com/',
    },
    {
      id: 'link',
      label: 'Link',
      href: provided.link || pageUrl || undefined,
    },
    {
      id: 'x',
      label: 'X',
      href:
        provided.x ||
        (pageUrl
          ? `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`
          : twitterPage(config?.siteTwitterHandle) || 'https://twitter.com/intent/tweet'),
    },
    {
      id: 'share',
      label: 'Share',
      href:
        provided.share ||
        (pageUrl
          ? `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
          : config?.siteFacebookPage || 'https://www.facebook.com/sharer/sharer.php'),
    },
    {
      id: 'copy',
      label: copied ? 'Link copied' : 'Copy link',
      href: provided.copy || pageUrl,
      copy: true,
    },
  ];

  const onCopy = async href => {
    const value = href || window.location.href;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      window.prompt('Copy this link', value);
    }
  };

  return (
    <BlockContainer id={blockId}>
      <ul>
        {items.map(item => {
          const Glyph = ICONS[item.id];
          return (
            <li key={item.id}>
              {item.copy ? (
                <button type="button" aria-label={item.label} onClick={() => onCopy(item.href)}>
                  <Glyph />
                </button>
              ) : (
                <a href={item.href} target="_blank" rel="noopener noreferrer" aria-label={item.label}>
                  <Glyph />
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </BlockContainer>
  );
};

export default JournalSocialLinks;
